import React, {useCallback, useEffect, useState} from 'react';
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import {Button, Divider, Flex, Form, message, Modal, Radio, Space, Steps, Table, Tag} from "antd";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {changeCurrentPage} from "../../../../../store/slices/tableController/ApplicationsListTableController";
import classes from "../application.module.css";
import {LocalName} from "../../../../../utils/LocalName";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import GreenButton from "../../../../../components/UI/Buttons/GreenButton";
import {getAllFormsPageable} from "../../../../../store/slices/FormsListSlice";
import FormService from "../../../../../services/FormService";
import {signContract} from "../../../../../utils/signContract";
import MyText from "../../../../../components/UI/Text/MyText";
import {Document, Page} from "react-pdf";
import {API_BASE_URL} from "../../../../../constants/api";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import DocumentService from "../../../../../services/DocumentService";
import dayjs from 'dayjs';
import {DatabaseOutlined, UserOutlined} from "@ant-design/icons";
import RedButton from "../../../../../components/UI/Buttons/RedButton";
import {clrs} from "../../../../../constants/colors";
import {countUnsignedForms} from "../../../../../store/slices/CounterSlice";

const {Step} = Steps;

// Очередь запрашивается всегда как директорская, а не по роли пользователя.
// Страницы проректора больше нет, список один: /api/form/get/rector вернул бы старую
// узкую выборку (только уже согласованные), и роль rector видела бы неполный список.
const QUEUE_TYPE = 'director';

const DirectorApplicationList = () => {
    const {user} = useSelector(state => state.user)
    const {forms, hasMore, isLoading} = useSelector(state => state.formsList)
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const formSave = useSelector(state => state.formSave);

    const {currentPage, pageSize} = useSelector(state => state.applicationsListTableController);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const hasSelected = selectedRowKeys.length > 0;

    const [filterForm] = Form.useForm();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [openViewContractModal, setOpenViewContractModal] = useState(false);
    const [viewContract, setViewContract] = useState(null);

    const [openViewEventHistory, setOpenViewEventHistory] = useState(false);
    const [viewEventHistory, setViewEventHistory] = useState(null);

    const [numPages, setNumPages] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [loadingDocument, setLoadingDocument] = useState(true);

    function onDocumentLoadSuccess({numPages}: { numPages: number }): void {
        setNumPages(numPages);
        setLoadingDocument(false);
    }

    const formatDate = (dateString) => {
        return dayjs(dateString).format('DD.MM.YYYY HH:mm');
    };

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename); // Set the download attribute with a filename
        document.body.appendChild(link);
        link.click();

        // Clean up and revoke the URL after download
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    async function downloadDocument(documentId) {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await DocumentService.getResourceById(documentId)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);

                triggerDownload(r.data, viewContract?.docDetails !== null ? viewContract?.docDetails + ".pdf" : "dogovor.pdf")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    }

    useEffect(() => {
        dispatch(getAllFormsPageable({
            page: currentPage,
            limit: pageSize,
            type: QUEUE_TYPE,
            categoryId: selectedCategoryId,
            applicationStatus: selectedStatus
        }))
    }, [navigate, selectedCategoryId, selectedStatus]);


    const categoriesList = useSelector(state => state.categoriesList);

    const fetchData = async (params) => {
        return dispatch(getAllFormsPageable({...params, type: QUEUE_TYPE, categoryId: selectedCategoryId, applicationStatus: selectedStatus}));
    }

    const saveCurrentPageSettings = (page, pageSize) => {
        dispatch(changeCurrentPage({page: page, pageSize: pageSize}));
    };

    /**
     * Одно действие директора = согласование + ЭЦП + договор.
     *
     * Раньше здесь был только directorApprove (N отдельных запросов), а подпись стояла
     * отдельным экраном у проректора. Теперь зовём батч-эндпоинт: он сам согласовывает
     * форму в статусе ADMIN_APPROVED прямо перед подписью. Один запрос на весь выбор —
     * атомарный результат и один тост с количеством вместо N независимых ответов.
     */
    async function handleSignContract(ids) {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await signContract("director", ids, null, null)
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(
                    getAllFormsPageable({
                        page: currentPage,
                        limit: pageSize,
                        type: QUEUE_TYPE,
                        categoryId: selectedCategoryId,
                        applicationStatus: selectedStatus
                    })
                );
                dispatch(countUnsignedForms({type: QUEUE_TYPE}));
                setSelectedRowKeys([]);
                filterForm.resetFields();
            });
    }

    async function handleRejectApplications(ids) {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await FormService.rejectFormsByIds(ids, user.id)
            .then((r) => {
                message.destroy();
                // message.success(<IntlMessage id={'success'}/>, 5);
            })
            .catch((reason) => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            }).finally(() => {
                dispatch(
                    getAllFormsPageable({
                        page: currentPage,
                        limit: pageSize,
                        type: QUEUE_TYPE,
                        categoryId: selectedCategoryId,
                        applicationStatus: selectedStatus
                    })
                );
                dispatch(countUnsignedForms({type: QUEUE_TYPE}));
                setSelectedRowKeys([]);
                filterForm.resetFields();
            });
    }

    const columns = [
        {
            // Сквозной номер строки — перенесён со страницы проректора вместе со списком.
            title: <IntlMessage id={'numberWithSymbol'}/>,
            render: (_, record) => (
                <p>{record.number}</p>
            ),
        },
        {
            title: <IntlMessage id={'categoryCourse'}/>,
            render: (_, record) => (
                <p>{LocalName.getName(record.category)}</p>
            ),
            width: '25%',
        },
        {
            title: <IntlMessage id={'date'}/>,
            render: (_, record) => (
                <p>{new Date(record.updatedAt).toLocaleDateString()}</p>
            ),
            width: '25%',
        },
        {
            title: <IntlMessage id={'fullName'}/>,
            render: (_, record) => (
                <p>{record.userData.fullName}</p>
            ),
            width: '25%',
        },
        {
            title: <IntlMessage id={'status'}/>,
            render: (_, record) => (
                <Flex style={{width: "100%"}} align={"center"} justify={"center"} gap={20}>
                    {record.applicationStatus === 'NEW' &&
                        <Tag color={clrs.green}><IntlMessage id={record.applicationStatus}/></Tag>
                    }
                    {record.applicationStatus === 'IN_PROGRESS' &&
                        <Tag color={clrs.blue}><IntlMessage id={record.applicationStatus}/></Tag>
                    }
                    {record.applicationStatus === 'FILLED' &&
                        <Tag color={clrs.grey}><IntlMessage id={record.applicationStatus}/></Tag>
                    }
                    {record.applicationStatus === 'REJECTED' &&
                        <Tag color={clrs.whiterRed}><IntlMessage id={record.applicationStatus}/></Tag>
                    }
                    {record.applicationStatus === 'COMPLETED' &&
                        <Tag color={clrs.blackerBlue}><IntlMessage id={"APPLICATION_COMPLETED"}/></Tag>
                    }
                    {record.applicationStatus === 'CLIENT_SIGNED' &&
                        <Tag color={clrs.blueForIcon}><IntlMessage id={"STUDENT_SIGNED"}/></Tag>
                    }
                    {record.applicationStatus === 'ADMIN_APPROVED' &&
                        <Tag color={clrs.orange} style={{color: "black"}}><IntlMessage id={record.applicationStatus}/></Tag>
                    }
                    {record.applicationStatus === 'DIRECTOR_APPROVED' &&
                        <Tag color={clrs.darkerGreen}><IntlMessage id={"DIRECTOR_AGREED"}/></Tag>
                    }
                    {record.applicationStatus === 'CONTRACT' &&
                        <Tag color={clrs.whiterGreen}><IntlMessage id={"CONTRACT_CREATED"}/></Tag>
                    }
                </Flex>
            ),
            width: '8%',
        },
        {
            title: <IntlMessage id={'form'}/>,
            render: (_, record) => (
                <>
                    <Flex style={{width: "100%"}} align={"center"} justify={"center"} wrap={"wrap"} gap={20}>
                        <p onClick={() => {
                            setViewEventHistory(record?.eventHistory?.events);
                            setOpenViewEventHistory(true);
                        }} className={classes.historyEventsBtn}><IntlMessage id={'eventHistory'}/></p>
                        <p onClick={() => {
                            setViewContract(record);
                            setOpenViewContractModal(true);
                        }} className={classes.copyLinkBtn}><IntlMessage id={'view'}/></p>
                        {/* Кнопку показываем, пока нет ЭЦП, а не пока нет согласования:
                            в очереди теперь и ADMIN_APPROVED, и уже согласованные
                            DIRECTOR_APPROVED — оба состояния закрываются этой же кнопкой. */}
                        {
                            !record.directorSignature &&
                            <p onClick={() => {
                                handleSignContract([record.id])
                            }} className={classes.signBtn}><IntlMessage id={'sign'}/></p>
                        }
                        {
                           record.directorSignature == null &&
                            <p onClick={() => {
                                handleRejectApplications([record.id]);
                            }} className={classes.rejectBtn}><IntlMessage id={'reject'}/></p>
                        }
                    </Flex>

                </>
            ),
            width: '25%',
        },
    ];

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
            <BlockLoading isLoading={isLoading}/>
            <Flex style={{width: "100%"}} align={"center"} justify={"center"} gap={20} wrap={"wrap"}>
                {
                    hasSelected &&
                    <GreenButton onClick={() => handleSignContract(selectedRowKeys)}>
                        <IntlMessage id={'sign'}/>
                    </GreenButton>
                }
                {
                    hasSelected &&
                    <RedButton onClick={() => handleRejectApplications(selectedRowKeys)}>
                        <IntlMessage id={'reject'}/>
                    </RedButton>
                }
            </Flex>
            <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
                <Form onKeyUp={(e) => {
                    if (e.key === "Enter") {
                        dispatch(getAllFormsPageable({
                            page: currentPage,
                            limit: pageSize,
                            type: QUEUE_TYPE,
                            categoryId: selectedCategoryId,
                            applicationStatus: selectedStatus
                        }))
                    }
                }} form={filterForm} className={classes.form}>
                    <Flex style={{width: "100%", position: "relative"}} align={"flex-start"} justify={"flex-start"}>
                        <Flex style={{width: "50%"}} vertical align={"flex-start"} justify={"flex-start"}>
                            <Divider className={classes.divider} orientation="left"><IntlMessage
                                id={'category'}/></Divider>
                            <Form.Item name="categoryId">
                                <Radio.Group value={selectedCategoryId}
                                             defaultValue={null}
                                             onChange={(e) => {
                                                 setSelectedCategoryId(e.target.value)
                                             }}>>
                                    <Space wrap={"wrap"}>
                                        <Radio value={null} key="all">
                                            <IntlMessage id={"allCategories"}/>
                                        </Radio>
                                        {
                                            categoriesList.categories.map((category, i) => {
                                                return (
                                                    <Radio value={category.id}
                                                           key={category.id}>{LocalName.getName(category)}</Radio>
                                                )
                                            })
                                        }
                                    </Space>
                                </Radio.Group>
                            </Form.Item>
                        </Flex>
                        {/*<Flex style={{width: "50%"}} vertical align={"flex-start"} justify={"flex-start"}>*/}
                        {/*    <Divider className={classes.divider} orientation="left"><IntlMessage id={'status'}/></Divider>*/}
                        {/*    <Form.Item name="status" >*/}
                        {/*        <Radio.Group  value={selectedStatus}*/}
                        {/*                      defaultValue={null}*/}
                        {/*                      onChange={(e) => {*/}
                        {/*                          setSelectedStatus(e.target.value)*/}
                        {/*                      }}>>*/}
                        {/*            <Space wrap={"wrap"}>*/}
                        {/*                <Radio value={null} ><Tag color={clrs.black}><IntlMessage id={"ALL"}/></Tag></Radio>*/}
                        {/*                <Radio value={"NEW"} ><Tag color={clrs.green}><IntlMessage id={"NEW"}/></Tag></Radio>*/}
                        {/*                <Radio value={"IN_PROGRESS"} ><Tag color={clrs.blue}><IntlMessage id={"IN_PROGRESS"}/></Tag></Radio>*/}
                        {/*                <Radio value={"FILLED"} ><Tag color={clrs.grey}><IntlMessage id={"FILLED"}/></Tag></Radio>*/}
                        {/*                <Radio value={"REJECTED"} ><Tag color={clrs.whiterRed}><IntlMessage id={"REJECTED"}/></Tag></Radio>*/}
                        {/*                <Radio value={"COMPLETED"} ><Tag color={clrs.blackerBlue}><IntlMessage id={"APPLICATION_COMPLETED"}/></Tag></Radio>*/}
                        {/*                <Radio value={"CLIENT_SIGNED"} ><Tag color={clrs.blueForIcon}><IntlMessage id={"STUDENT_SIGNED"}/></Tag></Radio>*/}
                        {/*                <Radio value={"ADMIN_APPROVED"} ><Tag color={clrs.orange} style={{color: "black"}}><IntlMessage id={"ADMIN_APPROVED"}/></Tag></Radio>*/}
                        {/*                <Radio value={"DIRECTOR_APPROVED"} ><Tag color={clrs.darkerGreen}><IntlMessage id={"DIRECTOR_AGREED"}/></Tag></Radio>*/}
                        {/*                <Radio value={"CONTRACT"} ><Tag color={clrs.whiterGreen}><IntlMessage id={"CONTRACT_CREATED"}/></Tag></Radio>*/}
                        {/*            </Space>*/}
                        {/*        </Radio.Group>*/}
                        {/*    </Form.Item>*/}
                        {/*</Flex>*/}
                    </Flex>
                </Form>
            </Flex>


            <MyTable
                isLoading={isLoading}
                dataSource={forms}
                columns={columns}
                fetchData={fetchData}
                saveCurrentPage={saveCurrentPageSettings}
                initialPage={currentPage}
                initialPageSize={pageSize}
                hasMore={hasMore}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
            />

            <Modal
                width={800}
                maskClosable={true}
                // title={<IntlMessage id={'contractsPage.details'}/>}
                open={openViewContractModal}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
                style={{
                    top: 20,
                }}
                onOk={() => {
                    setOpenViewContractModal(false)
                }}
                onCancel={() => {
                    setOpenViewContractModal(false)
                }}
                footer={[
                    <Button key="submit" type="primary" onClick={() => {
                        setOpenViewContractModal(false)
                    }}>
                        OK
                    </Button>
                ]}
            >
                {
                    viewContract &&
                    <Flex gap={50}
                          style={{
                              width: "100%",
                              minHeight: "calc(100vh - 300px)",
                              position: "relative",
                              padding: "0px 20px 20px 0px",
                          }}
                          vertical
                          align={"flex-start"}
                          justify={"flex-start"}>
                        <Flex align={"center"} justify={"center"} vertical
                              style={{overflowX: "auto", overflowY: "hidden", width: "100%"}}>
                            <Flex style={{
                                zIndex: 1,
                                padding: "10px 20px",
                                backgroundColor: "white",
                                borderRadius: 10,
                                boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
                            }} gap={15} justify={"space-between"} align={"center"}>
                                <Button disabled={pageNumber === 1} onClick={() => {
                                    setPageNumber(pageNumber - 1)
                                }}>
                                    {'<'}
                                </Button>
                                <MyText size={"small"}>
                                    {pageNumber} / {numPages}
                                </MyText>
                                <Button disabled={pageNumber === numPages} onClick={() => {
                                    setPageNumber(pageNumber + 1)
                                }}>
                                    {'>'}
                                </Button>
                            </Flex>
                            <Document file={API_BASE_URL + "/api/document/resource/" + viewContract?.documentId}
                                      onLoadSuccess={onDocumentLoadSuccess}
                                      loading={<IntlMessage id={'documentIsLoading'}/>}
                            >
                                <Page pageNumber={pageNumber} width={700}/>
                            </Document>
                        </Flex>
                        <Flex align={"flex-start"} justify={"center"} gap={20} style={{width: "100%"}}>
                            <GreyButton onClick={() => downloadDocument(viewContract?.documentId)}>
                                <IntlMessage id={'downloadContract'}/>
                            </GreyButton>
                        </Flex>
                    </Flex>
                }
            </Modal>
            <Modal
                width={800}
                maskClosable={true}
                // title={<IntlMessage id={'contractsPage.details'}/>}
                open={openViewEventHistory}
                // okText={"OK"}
                cancelText={<IntlMessage id={'close'}/>}
                style={{
                    top: 20,
                }}
                onOk={() => {
                    setOpenViewEventHistory(false)
                }}
                onCancel={() => {
                    setOpenViewEventHistory(false)
                }}
                footer={[
                    <Button key="submit" type="primary" onClick={() => {
                        setOpenViewEventHistory(false)
                    }}>
                        OK
                    </Button>
                ]}
            >
                {
                    viewEventHistory &&
                    <Steps progressDot direction="vertical"  current={viewEventHistory.length}>
                        {viewEventHistory.map((item, index) => (
                            <Step
                                key={index}
                                title={
                                    <Flex gap={"small"}>
                                        {item?.type === "SYSTEM" ? <DatabaseOutlined/> : <UserOutlined/>}
                                        <IntlMessage id={item?.activity}/> {!!item?.user &&" (" + item?.user?.profile?.shortFullName + ")"}
                                    </Flex>}
                                description={formatDate(item?.createdAt)}
                            />
                        ))
                        }
                    </Steps>
                }
            </Modal>
        </Flex>
    );
};

const MyTable = ({
                     isLoading,
                     dataSource,
                     columns,
                     fetchData,
                     saveCurrentPage,
                     initialPage = 1,
                     initialPageSize = 100,
                     hasMore = true,
                     expandable,
                     selectedRowKeys,
                     setSelectedRowKeys
                 }) => {
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);
    const [hasNextPage, setHasNextPage] = useState(hasMore);

    useEffect(() => {
        setHasNextPage(hasMore);
    }, [hasMore]);

    useEffect(() => {
        setCurrentPage(initialPage);
        setCurrentPageSize(initialPageSize);
    }, [initialPage, initialPageSize]);

    // Handle pagination change by updating the current page and page size.
    // Then, fetch data using the provided fetchData function.
    const handlePaginationChange = useCallback(
        (page, pageSize) => {
            setCurrentPage(page);
            setCurrentPageSize(pageSize);
            fetchData({page, limit: pageSize}).then((result) => {
                const hasMore = result.payload.hasMore;
                setHasNextPage(hasMore);
            });
            saveCurrentPage(page, pageSize);
        },
        [fetchData],
    );

    return (
        <Table
            rowSelection={rowSelection}
            bordered
            columns={columns}
            dataSource={dataSource}
            pagination={{
                current: currentPage,
                total: hasNextPage
                    ? currentPage * currentPageSize + 1
                    : currentPage * currentPageSize,
                defaultPageSize: initialPageSize,
                showSizeChanger: true,
                pageSize: currentPageSize,
                pageSizeOptions: ['5', '10', '20', '30', '50', '100'],
                onChange: handlePaginationChange,
                onShowSizeChange: handlePaginationChange,
            }}
            rowKey={record => record.id}
            loading={isLoading}
            style={{
                width: "100%",
            }}
            scroll={{x: 800}}
        />
    )
}

export default DirectorApplicationList;